// src/components/OfflineIndicator.tsx
import { useState, useEffect, useRef } from "react";
import { Notification, Box, Badge, Group, Text } from "@mantine/core";
import { IconWifiOff, IconWifi, IconCloudOff } from "@tabler/icons-react";
import { useOnline } from "../hooks/useOnline";

export function OfflineIndicator() {
  const isOnline = useOnline();
  const [showNotification, setShowNotification] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const prevOnlineRef = useRef<boolean | null>(null);

  useEffect(() => {
    // Don't show a notification on first mount; only on changes
    if (prevOnlineRef.current === null) {
      prevOnlineRef.current = isOnline;
      return;
    }

    if (prevOnlineRef.current !== isOnline) {
      setShowNotification(true);
      setIsVisible(true);
      const t = setTimeout(() => setShowNotification(false), 5000);
      prevOnlineRef.current = isOnline;
      return () => clearTimeout(t);
    }
  }, [isOnline]);

  if (!isVisible) return null;

  return (
    <>
      {/* Persistent status bar when offline */}
      {!isOnline && (
        <Box
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            backgroundColor: "#ff9800",
            color: "#fff",
            padding: "8px",
            textAlign: "center",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <Group justify="center" gap="xs">
            <IconCloudOff size={16} />
            <Text size="sm" fw={500}>
              You are offline. Viewing cached menu data.
            </Text>
            <Badge size="sm" variant="white" color="orange">
              Offline Mode
            </Badge>
          </Group>
        </Box>
      )}

      {/* Floating notification on change */}
      {showNotification && (
        <Box
          style={{
            position: "fixed",
            bottom: 80,
            right: 20,
            zIndex: 9999,
            maxWidth: 350,
            animation: "slideIn 0.3s ease-out",
          }}
        >
          <Notification
            icon={isOnline ? <IconWifi size={18} /> : <IconWifiOff size={18} />}
            color={isOnline ? "green" : "orange"}
            title={isOnline ? "Back Online" : "You are offline"}
            onClose={() => setShowNotification(false)}
            withCloseButton
            style={{
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              backgroundColor: isOnline ? "#e8f5e9" : "#fff3e0",
            }}
          >
            {isOnline
              ? "Your connection has been restored. Menu data is being updated."
              : "You are currently offline. Showing cached menu data."}
          </Notification>
        </Box>
      )}

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
}
